// Le chat avec Alfred (lot 3.3) : la dernière conversation, la réponse qui arrive en flux, et en petit ce que
// l'assistant a fait pendant le tour (« rappel créé : … »). Le modèle du VPS est lent (10 à 50 s) : l'écran le dit
// et compte les secondes plutôt que de laisser croire à une panne. Si l'assistant est indisponible, l'essentiel du
// jour s'affiche à la place.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Moi } from '@alfred/shared';
import type { Actions } from '../App';
import { Icone, Nav, Pastille } from '../components';
import { type ActionFaite, type MessageChat, NonConnecte, api } from '../lib/api';

/** La réponse en cours : le texte reçu, les actions faites, et le repli si l'assistant n'a pas pu répondre. */
interface EnCours {
  texte: string;
  actions: ActionFaite[];
  debut: number;
  indisponible?: { message: string; essentiel: string };
}

/** Les demandes de validation encore ouvertes, et de quoi les trancher depuis le chat. */
interface Validation {
  attente: Set<string>;
  trancher: (id: string, accepte: boolean) => void;
}

function ActionsFaites({ actions, validation }: { actions: ActionFaite[]; validation: Validation }) {
  if (!actions.length) return null;
  return (
    <ul className="chat-actions">
      {actions.map((a, k) => (
        <li key={k} className={a.demande ? 'a-valider' : a.ecriture ? 'ecriture' : undefined}>
          {a.ecriture && !a.demande && <Icone nom="coche" taille={12} />}
          {/* Un souvenir proposé se confirme à l'écran de la mémoire : un lien direct. */}
          {a.resume.startsWith('souvenir proposé') ? <a href="#/memoire">{a.resume} — à confirmer</a> : a.resume}
          {/* Une écriture retenue parce que l'échange a lu un contenu externe : la personne tranche ici. */}
          {a.demande && validation.attente.has(a.demande) && (
            <span className="boutons">
              <button className="btn petit principal" onClick={() => validation.trancher(a.demande!, true)}>Valider</button>
              <button className="btn petit" onClick={() => validation.trancher(a.demande!, false)}>Refuser</button>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Bulle({ m, validation }: { m: Pick<MessageChat, 'role' | 'contenu' | 'actions'>; validation: Validation }) {
  return (
    <div className={`chat-bulle ${m.role === 'user' ? 'moi' : 'alfred'}`}>
      {m.role === 'assistant' && <ActionsFaites actions={m.actions} validation={validation} />}
      {m.contenu && <p>{m.contenu}</p>}
    </div>
  );
}

export function Alfred({ moi, actions }: { moi: Moi; actions: Actions }) {
  const [conversation, setConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageChat[] | null>(null);
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState<EnCours | null>(null);
  const [secondes, setSecondes] = useState(0);
  const [aValider, setAValider] = useState<Set<string>>(new Set());

  // Les validations encore ouvertes : leurs boutons restent dans le fil tant qu'elles ne sont pas tranchées.
  const relireAttente = useCallback(() => {
    api.demandes().then((d) => setAValider(new Set(d.filter((x) => x.type === 'autorisation').map((x) => x.id)))).catch(() => undefined);
  }, []);
  useEffect(relireAttente, [relireAttente]);
  const champ = useRef<HTMLTextAreaElement>(null);

  const echec = useCallback((e: unknown) => {
    if (e instanceof NonConnecte) window.location.hash = '#/connexion';
    else actions.toast({ texte: (e as Error).message, erreur: true });
  }, [actions]);

  // La dernière conversation, pour reprendre où on en était (et retrouver une réponse arrivée pendant l'absence).
  const charger = useCallback(async (id?: string) => {
    try {
      const cible = id ?? (await api.conversations())[0]?.id;
      if (!cible) {
        setMessages([]);
        return;
      }
      const c = await api.conversation(cible);
      setConversation(c.conversation.id);
      setMessages(c.messages);
    } catch (e) {
      echec(e);
      setMessages([]);
    }
  }, [echec]);

  useEffect(() => {
    void charger();
  }, [charger]);

  useEffect(() => {
    // Le bas de la page, pas celui du fil : la saisie et la barre, collées en bas, cacheraient la dernière bulle.
    window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [messages, enCours?.texte, enCours?.actions.length, enCours?.indisponible]);

  // Le compteur de patience, tant que le premier mot n'est pas arrivé.
  const attente = enCours !== null && !enCours.texte && !enCours.indisponible;
  useEffect(() => {
    if (!attente) return;
    setSecondes(0);
    const t = window.setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [attente]);

  const envoyer = async () => {
    const message = saisie.trim();
    if (!message || (enCours && !enCours.indisponible)) return;
    setSaisie('');
    const provisoire: MessageChat = { id: `local-${Date.now()}`, role: 'user', contenu: message, actions: [], le: new Date().toISOString() };
    setMessages((ms) => [...(ms ?? []), provisoire]);
    const courant: EnCours = { texte: '', actions: [], debut: Date.now() };
    setEnCours(courant);
    let id = conversation;
    let indisponible = false;
    try {
      await api.discuter({ ...(id ? { conversationId: id } : {}), message }, (e) => {
        if (e.type === 'conversation') {
          id = e.id;
          setConversation(e.id);
        } else if (e.type === 'texte') courant.texte += e.texte;
        else if (e.type === 'annule') courant.texte = '';
        else if (e.type === 'outil') {
          courant.actions = [...courant.actions, { nom: e.nom, ecriture: e.ecriture, resume: e.resume, ...(e.demande ? { demande: e.demande } : {}) }];
          if (e.demande) setAValider((a) => new Set([...a, e.demande!]));
        }
        else if (e.type === 'indisponible') {
          indisponible = true;
          courant.indisponible = { message: e.message, essentiel: e.essentiel };
        }
        setEnCours({ ...courant });
      });
    } catch (e) {
      echec(e);
      setEnCours(null);
      return;
    }
    // La réponse est enregistrée par le cœur : on relit la conversation, qui fait foi.
    if (id) await charger(id);
    if (!indisponible) setEnCours(null);
    relireAttente();
    if (courant.actions.some((a) => a.ecriture)) void actions.recharger();
  };

  const nouvelle = () => {
    setConversation(null);
    setMessages([]);
    setEnCours(null);
    champ.current?.focus();
  };

  const appel = moi.membre.appellation ?? moi.membre.prenom;
  const validation: Validation = {
    attente: aValider,
    trancher: (id, accepte) => void actions.repondre(id, accepte).then(relireAttente),
  };

  return (
    <main className="ecran avec-nav chat">
      <div className="entete">
        <button className="carre44" onClick={nouvelle} aria-label="Nouvelle conversation" disabled={!messages?.length && !enCours}>
          <Icone nom="plus" />
        </button>
        <Pastille>Alfred</Pastille>
        <a className="carre44" href="#/reglages" aria-label="Réglages"><Icone nom="reglages" /></a>
      </div>

      <div className="chat-fil" aria-live="polite">
        {messages === null && <p className="message muted">Chargement…</p>}
        {messages?.length === 0 && !enCours && (
          <p className="message muted chat-vide">
            Bonjour, {appel}. Demandez-moi ce que vous avez aujourd’hui, de vous rappeler quelque chose, ou ce que dit votre cerveau sur un proche.
          </p>
        )}
        {messages?.map((m) => <Bulle key={m.id} m={m} validation={validation} />)}
        {enCours && !enCours.indisponible && (
          <div className="chat-bulle alfred">
            <ActionsFaites actions={enCours.actions} validation={validation} />
            {enCours.texte ? <p>{enCours.texte}</p> : (
              <p className="muted chat-attente">
                Alfred réfléchit<span className="points" />{secondes >= 5 && <small> {secondes} s — le modèle est lent, patience.</small>}
              </p>
            )}
          </div>
        )}
        {enCours?.indisponible && (
          <div className="chat-bulle alfred repli">
            <ActionsFaites actions={enCours.actions} validation={validation} />
            <p><strong>L’assistant est indisponible</strong> ({enCours.indisponible.message}). Voici l’essentiel :</p>
            <p className="essentiel">{enCours.indisponible.essentiel}</p>
          </div>
        )}
      </div>

      <form className="chat-saisie" onSubmit={(e) => { e.preventDefault(); void envoyer(); }}>
        <textarea
          ref={champ}
          rows={1}
          value={saisie}
          placeholder="Écrire à Alfred…"
          aria-label="Message à Alfred"
          maxLength={2000}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            // Entrée envoie au clavier physique ; sur iPhone, le bouton envoie et Entrée va à la ligne.
            if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
              e.preventDefault();
              void envoyer();
            }
          }}
        />
        <button className="btn principal" type="submit" aria-label="Envoyer" disabled={!saisie.trim() || (enCours !== null && !enCours.indisponible)}>
          <Icone nom="envoyer" />
        </button>
      </form>
      <Nav actif="alfred" sante={moi.modules.sante} />
    </main>
  );
}
